import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import SearchBar from "./SearchBar";
import Label from './form/Label';

const GenericDropdown = ({
    heading,
    labelComponent,
    tooltip,
    options = [],
    valueKey = 'value',
    displayValueKey = 'displayValue',
    defaultValue = '',
    placeholder = '--Select--',
    displaySelectedValue = true,
    addEmptyOption = false,
    onSelect = () => { },
    grow = false,
    readOnly = false,
    size,
    maxWidth,
    buttonTooltip = false,
    isRequired = false,
    toggleVariant = 'white',
    showSearchBar = false,
    maxOptionsDisplayed = 15,
    id,
    name,
    css = {},
}) => {
    const ref = useRef();
    const [translate] = useTranslation();
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const [searchPrompt, setSearchPrompt] = useState('');

    useEffect(() => {
        setSelectedValue(defaultValue);
    }, [defaultValue]);

    const allOptions = useMemo(() => {
        const result = options.map((option) => option);

        if (addEmptyOption) {
            result.unshift({
                [valueKey]: '',
                [displayValueKey]: placeholder,
            });
        }

        return result;
    }, [addEmptyOption, valueKey, displayValueKey, placeholder, options]);

    const getOptionDisplay = useCallback((value) => {
        const option = allOptions.find((option) => option[valueKey] === value);

        return (option && option[displayValueKey]) || value;
    }, [allOptions, valueKey, displayValueKey]);

    const selectedDisplayValue = useMemo(() => {
        if (!displaySelectedValue) {
            return getOptionDisplay('') || placeholder;
        }

        let result;

        if (selectedValue !== undefined && selectedValue !== null) {
            result = getOptionDisplay(selectedValue);
        }

        return result || getOptionDisplay('') || placeholder;
    }, [displaySelectedValue, getOptionDisplay, selectedValue, placeholder]);

    const handleChange = useCallback((eventKey) => {
        setSelectedValue(eventKey);
        onSelect(eventKey);
    }, [setSelectedValue, onSelect]);

    const toggle = useMemo(() => {
        return (
            <Dropdown.Toggle
                variant={toggleVariant}
                size={size}
                className={`border ${css.toggle || ''} ${grow ? 'd-flex justify-content-between align-items-center flex-grow-1' : ''}`}
                disabled={readOnly || allOptions.length === (addEmptyOption ? 1 : 0)}
                style={{ width: maxWidth }}
            >
                {selectedDisplayValue}
            </Dropdown.Toggle>
        );
    }, [selectedDisplayValue, readOnly, allOptions, size, maxWidth, grow, toggleVariant, css.toggle, addEmptyOption])

    const filterFunction = useCallback(option => {
        if (searchPrompt) {
            return option[displayValueKey]?.toLowerCase().includes(searchPrompt.toLowerCase())
        }
        return true;
    }, [displayValueKey, searchPrompt]);

    const dropdownOptions = useMemo(() => {
        return (
            <Dropdown.Menu className={`${grow ? "w-100" : ""}`}>
                {showSearchBar && <div className='pb-2 px-2'>
                    <SearchBar ref={ref} size="sm" initialValue={searchPrompt} placeholder={translate("OTM_SEARCH")} onChange={(value) => setSearchPrompt(value)} />
                </div>}
                {allOptions.filter(filterFunction).map((option, index) => {
                    if (showSearchBar && index >= maxOptionsDisplayed) {
                        return null;
                    }

                    if (option.isDivider) {
                        return <Dropdown.Divider key={index} />
                    }

                    const value = option[valueKey];
                    const displayValue = getOptionDisplay(value);

                    return (
                        <Dropdown.Item
                            key={index}
                            eventKey={value}
                            active={selectedValue === value}
                            className={`${size === "sm" ? "small" : ""}`}
                        >
                            {'' + displayValue}
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        );
    }, [allOptions, maxOptionsDisplayed, valueKey, selectedValue, searchPrompt, showSearchBar, grow, size, filterFunction, translate, getOptionDisplay]);

    const dropdown = useMemo(() => {
        return (
            <Dropdown
                onSelect={handleChange}
                id='dropdown'
                className={`${css.dropdown || ''} ${grow ? 'd-flex' : ''}`}
            >
                {toggle}
                {dropdownOptions}
            </Dropdown>
        );
    }, [handleChange, toggle, dropdownOptions, grow, css.dropdown]);

    return (
        <>
            <Label
                controlId={id || name}
                label={heading}
                labelComponent={labelComponent}
                tooltip={tooltip}
                buttonTooltip={buttonTooltip}
                isRequired={isRequired}
            />
            {dropdown}
        </>
    );
};

export default GenericDropdown;